import { Component } from 'react';
import type { ErrorInfo } from 'react';
import { AlertTriangle, RefreshCw, Home, Bug, Copy } from 'lucide-react';
import { Button } from './button';
import { Card, CardContent, CardHeader, CardTitle } from './card';
import { Badge } from './badge';
import { toast } from 'sonner';
import type { ErrorLog, ErrorBoundaryProps, ErrorBoundaryState } from '../../shared/types/error-types';

// Error logging service
class ErrorLogger {
  private static instance: ErrorLogger;
  private logs: ErrorLog[] = [];
  private maxLogs = 100;

  static getInstance(): ErrorLogger {
    if (!ErrorLogger.instance) {
      ErrorLogger.instance = new ErrorLogger();
    }
    return ErrorLogger.instance;
  }

  log(error: Error, errorInfo: ErrorInfo, context?: string): void {
    const errorLog: ErrorLog = {
      id: Date.now().toString(),
      timestamp: new Date().toISOString(),
      error: {
        name: error.name,
        message: error.message,
        stack: error.stack,
      },
      errorInfo: {
        componentStack: errorInfo.componentStack || '',
      },
      context,
      userAgent: navigator.userAgent,
      url: window.location.href,
      userId: this.getCurrentUserId(),
    };

    this.logs.unshift(errorLog);
    
    // Keep only the most recent logs
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(0, this.maxLogs);
    }

    // Store in localStorage for persistence
    try {
      localStorage.setItem('errorLogs', JSON.stringify(this.logs));
    } catch (e) {
      console.warn('Failed to store error logs in localStorage:', e);
    }

    // Log to console in development
    if (import.meta.env.DEV) {
      console.error('Error logged:', errorLog);
    }

    // In production, you might want to send to an external service
    if (import.meta.env.PROD) {
      this.sendToExternalService(errorLog);
    }
  }

  private getCurrentUserId(): string | undefined {
    try {
      // Try to get user ID from auth store or localStorage
      const authData = localStorage.getItem('auth-storage');
      if (authData) {
        const parsed = JSON.parse(authData);
        return parsed.state?.user?.id;
      }
    } catch (e) {
      // Ignore errors when getting user ID
    }
    return undefined;
  }

  private async sendToExternalService(errorLog: ErrorLog): Promise<void> {
    try {
      // This would be your error reporting service (e.g., Sentry, LogRocket, etc.)
      // For now, we'll just log it
      console.log('Would send to external service:', errorLog);
      
      // Example implementation:
      // await fetch('/api/errors', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(errorLog),
      // });
    } catch (e) {
      console.warn('Failed to send error to external service:', e);
    }
  }

  getLogs(): ErrorLog[] {
    return [...this.logs];
  }

  clearLogs(): void {
    this.logs = [];
    localStorage.removeItem('errorLogs');
  }

  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }
}



type Props = ErrorBoundaryProps;
type State = ErrorBoundaryState;

export class ErrorBoundary extends Component<Props, State> {
  private errorLogger = ErrorLogger.getInstance();

  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { 
      hasError: true, 
      error,
      errorId: Date.now().toString(),
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Log the error
    this.errorLogger.log(error, errorInfo, this.props.context);
    
    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);
    
    // Update state with error info
    this.setState({ errorInfo });
  }

  private handleReset = () => {
    this.setState({ 
      hasError: false, 
      error: undefined, 
      errorInfo: undefined,
      errorId: undefined,
    });
  };

  private handleReload = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  private handleCopyError = async () => {
    if (!this.state.error || !this.state.errorInfo) return;

    const errorDetails = {
      id: this.state.errorId,
      timestamp: new Date().toISOString(),
      error: {
        name: this.state.error.name,
        message: this.state.error.message,
        stack: this.state.error.stack,
      },
      componentStack: this.state.errorInfo.componentStack,
      context: this.props.context,
      url: window.location.href,
      userAgent: navigator.userAgent,
    };

    try {
      await navigator.clipboard.writeText(JSON.stringify(errorDetails, null, 2));
      toast.success('Error details copied to clipboard');
    } catch (e) {
      toast.error('Failed to copy error details');
    }
  };

  private renderErrorFallback() {
    const { level = 'component' } = this.props;
    const { error, errorId } = this.state;

    // Different layouts based on error level
    if (level === 'page') {
      return (
        <div className="min-h-screen flex items-center justify-center p-4">
          <Card className="max-w-lg w-full">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="h-6 w-6" />
                Page Error
              </CardTitle>
              {errorId && (
                <Badge variant="outline" className="w-fit">
                  Error ID: {errorId}
                </Badge>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                This page encountered an unexpected error. You can try refreshing the page or return to the home page.
              </p>
              
              {error && (
                <details className="text-sm">
                  <summary className="cursor-pointer font-medium">Technical Details</summary>
                  <div className="mt-2 p-3 bg-muted rounded text-xs font-mono overflow-auto max-h-32">
                    <div className="font-semibold">{error.name}: {error.message}</div>
                    {error.stack && (
                      <pre className="mt-2 whitespace-pre-wrap">{error.stack}</pre>
                    )}
                  </div>
                </details>
              )}

              <div className="flex flex-wrap gap-2">
                <Button onClick={this.handleReload} className="flex-1">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Reload Page
                </Button>
                <Button onClick={this.handleGoHome} variant="outline" className="flex-1">
                  <Home className="h-4 w-4 mr-2" />
                  Go Home
                </Button>
                <Button onClick={this.handleCopyError} variant="ghost" size="sm">
                  <Copy className="h-4 w-4 mr-2" />
                  Copy Error
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    if (level === 'widget') {
      return (
        <div className="p-4 border border-destructive/20 rounded-lg bg-destructive/5">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-destructive">Widget Error</p>
              <p className="text-xs text-muted-foreground mt-1">
                {error?.message || 'This widget encountered an error'}
              </p>
              <div className="flex gap-2 mt-2">
                <Button onClick={this.handleReset} variant="outline" size="sm">
                  <RefreshCw className="h-3 w-3 mr-1" />
                  Retry
                </Button>
                {errorId && (
                  <Button onClick={this.handleCopyError} variant="ghost" size="sm">
                    <Bug className="h-3 w-3 mr-1" />
                    Report
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Default component level error
    return (
      <Card className="max-w-md mx-auto mt-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Component Error
          </CardTitle>
          {errorId && (
            <Badge variant="outline" className="w-fit">
              Error ID: {errorId}
            </Badge>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            This component encountered an unexpected error. You can try again or refresh the page.
          </p>
          
          {error && (
            <details className="text-xs text-muted-foreground">
              <summary className="cursor-pointer">Technical Details</summary>
              <pre className="mt-2 p-2 bg-muted rounded text-xs overflow-auto">
                {error.message}
                {error.stack && '\n\n' + error.stack}
              </pre>
            </details>
          )}
          
          <div className="flex gap-2">
            <Button onClick={this.handleReset} variant="outline" size="sm">
              <RefreshCw className="h-4 w-4 mr-2" />
              Try Again
            </Button>
            <Button onClick={this.handleReload} size="sm">
              <RefreshCw className="h-4 w-4 mr-2" />
              Reload Page
            </Button>
            <Button onClick={this.handleCopyError} variant="ghost" size="sm">
              <Copy className="h-4 w-4 mr-2" />
              Copy
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return this.renderErrorFallback();
    }

    return this.props.children;
  }
}

// Export the error logger for use in other parts of the app
export { ErrorLogger };

// Convenience components for different error boundary levels
export const PageErrorBoundary: React.FC<Omit<Props, 'level'>> = (props) => (
  <ErrorBoundary {...props} level="page" />
);

export const WidgetErrorBoundary: React.FC<Omit<Props, 'level'>> = (props) => (
  <ErrorBoundary {...props} level="widget" />
);

export const ComponentErrorBoundary: React.FC<Omit<Props, 'level'>> = (props) => (
  <ErrorBoundary {...props} level="component" />
);